"use strict";

/* ---------------------------------------------------IMPORTS IN WAITING------------------------------------------------------
 *                                (functions that SHOULD by all rights be imported modules)                                 */

// generate a (usually) 9-digit string of numbers and letters
// https://gist.github.com/gordonbrander/2230317
const uniqueID = function (prefix = "_") {
  return prefix + Math.random().toString(36).substr(2, 9);
};

// simple sanity-checker. Other types of "objects" to not stringify to [object Object],
// for example arrays are typeof "object" but do not pass this check.
const isPlainObject = function (obj) {
  return Object.prototype.toString.call(obj) === "[object Object]";
};

// Promise.any polyfill
// https://github.com/ungap/promise-any
var any = (
  Promise.any ||
  function ($) {
    return new Promise(function (D, E, A, L) {
      A = [];
      L = $.map(function ($, i) {
        return Promise.resolve($).then(D, function (O) {
          return ((A[i] = O), --L) || E({ errors: A });
        });
      }).length;
    });
  }
).bind(Promise);

/* ----------------------------------------------------- END IMPORTS IN WAITING  --------------------------------------------*/

/*
 * The TransferAgent HTTP Launcher in a nutshell:
 *
 * 1. create a new instance by passing in a proxy configuration (or not) to the HTTP_Launcher constructor
 * 2. the "launch" method is called explicitly in order to allow for the promises API to work. The calling application needs to be able
 *    to "then"/"catch" the attempt.
 * 3. the iFrame-based protocol handler invoker BLINDLY (because there is no choice) tries to launch TA (with proxy config if provided)
 * 4. Heartbeat is polled until it succeeds, which tells us that the launch worked
 * 5. If polling ends with no success, we can report the failure
 * 6. The launch mechanism returns a "thenable" promise, allowing the calling application to decide what to do with the results.
 *
 * eg.
 *
 * const launcher = new HTTP_Launcher(proxy);
 * launcher
 *      .launch();
 *
 * launcher.pollHeartbeat()
 *      .then(successHandler)
 *      .catch(errorHandler)
 *
 * By design, if you have reached an error state and want to try another attempt, you should destroy the original instance and
 * make a new one (by re-using the variable in the app, you will allow the old instance to be GC'd). For example, when allowing
 * the user to provide another port to attempt connection on.
 */

/**
 * HTTP_Launcher is invoked with the new keyword to create an object
 * that provides the functionality commented above.
 */
export default class HTTP_Launcher {
  /**
   *
   * @param {Array.<String>}  urls        - An array of URL strings for the launcher to test heartbeat on.
   * @param {Object}          proxy       - Object literal configuring the fallback proxy (for when localhost not available)
   *                                        NOTE: a future refactor could normalize and use fake overloading to utilize array of proxies.
   * @param {Object}          pollConfig  - Plain object of config params to control polling mechanism (graceTime, maxTime, preferProxy)
   */
  constructor(urls, proxy, pollConfig) {
    // sanity check urls for being an array; when single url, a string is permitted
    if (typeof urls === "string") {
      urls = [urls];
    }

    // store the values passed in from the constructor call and eliminate undefined error on them
    this.urls = urls || [];
    this.proxy = proxy || {};
    this.pollConfig = pollConfig || {};
    this.basePath = "";

    // other properties that we are going to keep track of or use during launch process
    this.heartbeatStartTime = null;
    this.foundHeartbeat = false;
    this.protocolHandlerURL = "filecatalyst://localhost";
    this.launchErrors = new Set();

    // prepare an internal state object with defaults, mainly for retrieving from localStorage
    this.defaultProperties = {
      ta_found: false,
      port: this.getDefaultPort(),
    };

    // establish proxy defaults and then merge them with values passed in
    const proxyDefaults = {
      proxyID: this.getLocal("proxyID") || uniqueID(),
      proxyHost: "",
      proxyPort: 8080,
      proxySSL: false,
      proxyPath: "/servlet/atmosphere",
    };

    // if we have supplied a proxy, replace the empty object with a merged
    // proxy + defaults object.
    if (isPlainObject(this.proxy)) {
      this.proxy = Object.assign(proxyDefaults, this.proxy);
    }

    // establish polling options and then merge them with values passed in
    // if isCentral is not explicitly passed in, we check for existence of nodeID
    // within a centralNewHost object if one is found. This is potentially stomped by
    // passed-in config object
    const pollConfigDefaults = {
      graceTime: 2000,
      maxTime: 60000,
      preferProxy: false,
      isCentral: (function () {
        return (
          typeof centralNewHost !== "undefined" &&
          centralNewHost.nodeID.length > 0
        );
      })(),
    };

    if (isPlainObject(this.pollConfig)) {
      this.pollConfig = Object.assign(pollConfigDefaults, this.pollConfig);
    }

    /**
     * updateProtocolHandlerURL builds, stores (this.protocolHandlerURL) and returns a URL
     * for initializing the protocol handler. Must check for valid proxy values before
     * bothering; otherwise the protocolHandlerURL stays in default mode and is returned.
     *
     * This is defined in the constructor because we need it for construction.
     *
     * @param {Object} [proxyParams] - Optionally pass in new proxy parameters to update the
     *                                 protocol handler URL. If not supplied, use the ones
     *                                 from when object was constructed.
     */
    this.updateProtocolHandlerURL = function (proxyParams) {
      // cache the instance object to crush scope confusion of "this" for utilities
      const myself = this;

      // merge incoming proxy paramaters into the ones stored in the instance.
      // Note that Object.assign updates the target (myself.proxy) and returns it
      let proxy = Object.assign(myself.proxy, proxyParams);

      // only paramaterize the proxy if one exists.
      if (myself._validProxy(proxy)) {
        let url = myself.protocolHandlerURL + "?";

        // we need to append the term "websocket" to proxyPath
        let proxyCopy = Object.assign({}, proxy);

        proxyCopy.proxyPath +=
          proxyCopy.proxyPath.charAt(proxyCopy.proxyPath.length - 1) == "/"
            ? ""
            : "/";
        proxyCopy.proxyPath += "websocket";

        // proxyCopy is updated; supply it as query string
        url += new URLSearchParams(proxyCopy).toString();

        // update the stored URL in case we need it again
        myself.protocolHandlerURL = url;
      }

      // might as well return the updated URL as well
      return myself.protocolHandlerURL;
    };

    // for a proxy to be valid, it must have a hostname and port.
    this._validProxy = function (proxy = this.proxy) {
      let valid = true;

      if (
        typeof proxy.proxyHost !== "string" ||
        typeof proxy.proxyPort !== "number" ||
        proxy.proxyPath.length <= 0
      ) {
        valid = false;
      }

      return valid;
    };

    // conditionally run the update method, which may produce a new protocolHandlerURL
    if (this._validProxy()) {
      this.updateProtocolHandlerURL(this.proxy);
    }
  }

  // ----------------------- end constructor -------------------------

  // ----------------------- UTILITIES -------------------------------
  /**
   * A setter for the TransferAgent launcher properties. We use localStorage with a stringified object
   * stored at ta_properties as the source of truth. All setting and getting goes through this localStorage
   * entry.
   *
   * The method is defined in the constructor because we need it during construction.
   * @param {String}  key     - A key for a property to store within ta_properties in localStorage
   * @param {String}  value   - The value for the key being stored within ta_properties
   */
  setLocal(key, value) {
    // use localStorage as the source of truth, or grab defaults
    let properties =
      JSON.parse(localStorage.getItem("ta_properties")) ||
      this.defaultProperties;
    properties = properties ? properties : {};
    // merge in the defaults; worst-case scenario, this is redundant.
    properties = this.defaultProperties
      ? Object.assign(this.defaultProperties, properties)
      : properties;

    // if we have passed in a key/value, we need to update the local object
    // before storing it. Otherwise, just store the current object
    if (typeof key === "string" && typeof value !== "undefined") {
      properties[key] = value;
    }

    // store the memory object, whether it was modified or not
    localStorage.setItem("ta_properties", JSON.stringify(properties));

    // might as well return a copy of the properties
    return properties;
  }

  /**
   * A getter for the TransferAgent launcher properties, using localStorage entry "ta_properties", which
   * is treated as the source of truth. Will either dig out a particular value based on key, or return all
   * of ta_properties.
   *
   * The method is defined in the constructor because we need it during construction.
   *
   * @param {String}  key -   The key used to find a value stored within ta_properties. If not provided,
   *                          will retrieve all of ta_properties from localStorage
   */
  getLocal(key) {
    var props = localStorage.getItem("ta_properties");
    props = JSON.parse(props);

    // if JSON didn't parse, we still have the defaults
    if (props === null) {
      props = this.defaultProperties;
    }

    // if there is no key, return all of the properties
    if (typeof key === "undefined") {
      return props;
    } else {
      // return the keyed property (will simply be undefined if nonexistant)
      return props[key];
    }
  }

  /**
   * Checks to see if the context is that of the tray application then pick default port based on current URL.
   * @returns Returns the default port.
   */
  getDefaultPort() {
    let defaultPort = 12680;
    if (this.isTrayApp()) {
      const trayPort =
        location.port || (location.protocol === "https:" ? "443" : "80");
      defaultPort = isNaN(parseInt(trayPort, 10))
        ? 12680
        : parseInt(trayPort, 10);
      // we now have the up to date port so go ahead and override the one in localStore, which may not be the correct port anymore
      // because user may have changes the port TA runs on.
      this.setLocal("port", defaultPort);
    }
    return defaultPort;
  }

  /**
   *
   * @returns Parsed port number from @urls if any of the urls come from the tray application.
   */
  isTrayApp() {
    return (
      document.getElementsByTagName("body")[0].getAttribute("data-apptype") ===
      "bundled-with-ta"
    );
  }

  /**
   * We juggle X# of URLs (usually two) at a given time. Since the entire
   * purpose of the HTTP_Launcher is to know whether to fall back from
   * localhost or not, we need to know if a URL being used is "localhost"
   *
   * @param {String} url - True if a given URL contains our "localhost" domain,
   *                       which is expected to be localhost.filecatalyst.net
   */
  _urlIsLocalhost(url) {
    return url.toLowerCase().indexOf(this.local.hostname) >= 0;
  }

  /**
   * A successful heartbeat check means that we know what our base path for
   * REST calls should be; make this available to the app using HTTP_Launcher
   *
   * @param {String} url - a candidate for updating the successful heartbeat URL
   */
  _updatebasePath(url) {
    let performUpdate = false;

    // no brainer... if we don't have one, update. If this hasn't yet been
    // replaced, it ALSO means it was the fastest one to return, so go ahead
    // and update.
    if (this.basePath == "") {
      performUpdate = true;
    }

    // if we prefer proxy and we actually get one, let's store it!
    if (
      this.pollConfig.preferProxy == true &&
      this._validProxy() &&
      !this._urlIsLocalhost(url)
    ) {
      performUpdate = true;
    }

    if (performUpdate == true) {
      // we don't need the full heartbeat endpoint, just
      // the base URL, so strip out the extra stuff
      url = url.replace("/rs/agent/heartbeat", "");
      this.basePath = url;
      console.info(`Update the base path to ${this.basePath}`);
    }
  }

  /**
   * Returns true when maxTime milliseconds have passed since launch
   *
   * @param {Number} durationOverride  -  When called with a number passed in, we explicitly set the
   *                                      duration to be considered "timed out"; otherwise it uses configured
   *                                      maxTime value.
   */
  _timedOut(durationOverride) {
    let timedOut = false;

    const targetInterval = durationOverride || this.pollConfig.maxTime;
    const now = Date.now();
    const gap = now - this.heartbeatStartTime;

    if (gap >= targetInterval) {
      timedOut = true;
    }

    return timedOut;
  }

  /**
   * Based on a "base" URL, append the rest of the path required to reach
   * a REST endpoint for a heartbeat check.
   *
   * @param {String} base - the scheme, domain, port, and path which serve
   *                        as a prefix to the REST endpoint
   */
  _buildHeartbeatURL(base) {
    // normalize base to have trailing slash
    if (base.substr(-1) !== "/") {
      base += "/";
    }

    // This was needed because we no longer pass 'local' URL as its own
    // config object; yet, the user may update ports.
    if (base.indexOf("localhost") >= 0 || base.indexOf("127.0.0.1") >= 0) {
      let url = new URL(base);
      url.port = this.getLocal("port");
      base = url.toString();
    }

    return base + "rs/agent/heartbeat";
  }

  /**
   * Returns the proxy endpoint for a heartbeat check.
   */
  _buildProxyHeartbeatURL() {
    let base = "";

    // proxy was already stored during construction. Let's store it in a variable.
    let proxy = this.proxy;

    if (this._validProxy()) {
      // auto-detect scheme from window.location. In the case where this CREATES
      // an invalid scheme, we're no worse off because the browser wouldn't have
      // allowed it anyhow. (if your proxy is HTTP but your app is HTTPS, you're out of luck)
      let scheme = window.location.protocol + "//";

      // find out if we need to append a slash to the path or not
      let pathAppend =
        proxy.proxyPath.charAt(proxy.proxyPath.length - 1) == "/" ? "" : "/";
      pathAppend += "proxy/";

      base +=
        scheme +
        proxy.proxyHost +
        ":" +
        proxy.proxyPort +
        "/" +
        proxy.proxyPath +
        pathAppend +
        proxy.proxyID;
    }

    return this._buildHeartbeatURL(base);
  }

  /**
   * Returns a list of available heartbeats. Sometimes it is only one, but we
   * normalize to list for internal consistency.
   */
  _heartbeatURLs() {
    let heartbeats = this.urls.map((url) => this._buildHeartbeatURL(url));

    // If there was no proxy submitted during construction, we don't need
    // to invoke a protocol handler with proxy ID.
    if (this._validProxy()) {
      this.setLocal("proxyID", this.proxy.proxyID);
      heartbeats.push(this._buildProxyHeartbeatURL());
    }

    return heartbeats;
  }

  // --------------------- public methods and ASYNC functions ----------------------

  /**
   * Invoking the protocol handler is done at least once, when the constructor is
   * run. However, this also intended as a public method in case we need to
   * invoke it again from the application.
   */
  invokeProtocolHandler() {
    try {
      // use an existing iFrame if possible.
      var iFrame = $("#hiddenIframe");

      if (iFrame.length <= 0) {
        iFrame = $(
          '<iframe id="hiddenIframe" src="about:blank" style="display:none"></iframe>'
        ).appendTo("body");
      }

      // updating the src attribute triggers the protocol handler to be called, similarly to if
      // a user had clicked a protocol handler-enabled link.
      iFrame.attr("src", "about:blank");
      iFrame.attr("src", this.protocolHandlerURL);
    } catch (e) {
      console.error(e);

      // need to move this to a failure callback
      //pg.ui.spinnerScreen(false, 'connecting');
    }
  }

  launch() {
    this.launchErrors.clear();

    // so, launching is the very very wrong thing to do if this is run from
    // within Central. So, let's foil any accidental attempts (ie. the app can't
    // be expected to remember EVERYTHING)
    if (this.pollConfig.isCentral === false) {
      this.invokeProtocolHandler();
    }

    // return instance for chaining
    return this;
  }

  /**
   * _checkEndpoint is a wrapping function, encapsulating the actual "fetch"
   * operation for a single REST URL (ie. heartbeat). It contains a polling interval method,
   * a while loop for polling until conditions are met, and ultimately handles a
   * "grace period" (ie. giving proxy a chance to succeed) before returning results
   * to the checkEndpoints wrapper.
   *
   * @param {String} url - the full URL to be checked for heartbeat
   */
  async _checkEndpoint(url, duration) {
    // cache the object's scope
    const myself = this;

    /**
     *
     * @param {Number} ms - A number representing how many milliseconds to
     *                      wait before resolving a promise. Used to create
     *                      polling delays.
     */
    function wait(ms = 1000) {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    }

    /**
     * The actual polling functionality. It calls the fetch, which is
     * either successful or not. The results are analyzed and returned
     * or thrown to a try/catch block running in a loop.
     */
    async function pollingFunction() {
      let result;

      // perform the fetch itself
      try {
        result = await fetch(url, { cache: "no-store" });
      } catch (err) {
        result = err;
      }

      // analyse the results of the fetch and return or throw.
      if (result && result.ok == true) {
        myself.setLocal("ta_found", "true");
        myself.foundDuringPoll = true;
        myself._updatebasePath(result.url);

        return result;
      } else {
        let entry = result.toString();

        // in the case of networking errors, the result is
        // an unresolved Promise or Response.
        if (entry.startsWith("[") && entry.endsWith("]")) {
          entry = "unresolved error";
        }

        myself.launchErrors.add(entry);
        throw result;
      }
    }

    let whileResult = "No results returned";

    // keep firing until condition is met. Do not throw from the catch
    // block, but hold onto it until after while loop.
    while (
      myself.foundDuringPoll === false &&
      myself._timedOut(duration) == false
    ) {
      try {
        await wait(); // possibly an unecessary delay. Re-write candidate.
        let value = await pollingFunction();
        whileResult = { value };
      } catch (reason) {
        whileResult = { reason };
      }
    }

    // the while loop has completed, but we may still need to wait for the graceTime
    // before returning or throwing. This only happens for:
    // 1. proxy configured; 2. proxy is preferred; 3. current URL is localhost.
    if (
      myself._validProxy() == true &&
      myself.pollConfig.preferProxy == true &&
      myself._urlIsLocalhost(url)
    ) {
      await wait(myself.pollConfig.graceTime);
    }

    // the optional wait is over, so let's either throw or return to complete the promise
    if (whileResult.reason) {
      throw whileResult.reason;
    }

    return whileResult.value;
  }

  /**
   * This wrapper around _checkEndpoint is necessary so that we can listen for "any"
   * promises being resolved. "any" is preferred to "all" because "all" would required failing
   * URLs to fully time out. "any" allows a successful attempt to return to the main application.
   *
   * @param {Array} urls - An array of one or more strings representing full URLs to check
   *                       for successful response
   */
  async checkEndpoints(urls, duration) {
    const promises = [];

    for (let url of urls) {
      promises.push(this._checkEndpoint(url, duration));
    }

    // return the Promise so that it can be resolved at the level
    // of the caller instead of here.
    return await Promise.any(promises);
  }

  /**
   * This is the entry point from the application using HTTP_Launcher. After creating a new
   * HTTP_Launcher, the application can chain a ".then(result)" handler for success or a
   * ".catch(result)" handler for errors. The result object passed up contains the successful
   * base path for REST calls, or a Set of errors for why the operation failed.
   *
   * @param {Number} duration - The length of time this particular attempt at launching
   *                            should take. Serves as an override for default time
   *                            NOTE: a value of zero can be used to indicate doing
   *                            a "quick check"
   */
  async pollHeartbeat(duration) {
    const myself = this;

    this.heartbeatStartTime = Date.now();
    this.foundDuringPoll = false;

    // we will always at least try localhost; a proxy will still take effect if attempts fail
    const heartbeats = this._heartbeatURLs();

    let result;

    // invoke the "heartbeats" wrapper. The try/catch block allows us to listen for
    // the resolution or rejection of the chain of Promises being made. However, we
    // only store them, we do not return/throw further YET.
    try {
      result = await myself.checkEndpoints(heartbeats, duration);
    } catch (e) {
      result = e;
    }

    // analyse the results of the heartbeat checks, and return a success or throw an error.
    // This is consumed by the application that is using HTTP_Launcher.
    if (result.ok) {
      return result;
    } else {
      throw myself.launchErrors;
    }
  }

  // this defied aliasing launch() or getHeartbeats() wholesale, so some parts are identical
  async quickCheck() {
    let myself = this;

    var urls = myself._heartbeatURLs();
    let promiseList = [];

    for (let url of urls) {
      promiseList.push(fetch(url, { cache: "no-store" }));
    }

    let response = await Promise.any(promiseList);

    // analyse the results of the heartbeat checks, and return a success or throw an error.
    // This is consumed by the application that is using HTTP_Launcher.
    if (response && response.ok == true) {
      myself.setLocal("ta_found", true);
      myself._updatebasePath(response.url);
      return response;
    } else {
      throw response;
    }
  }
}
